import { ReactNode, useRef } from 'react';
import { css } from '@emotion/react';
import { Button } from '@frontend/generic-dialpad-accessories';
import { Trans, useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { ContentLoader, DialPadErrorState, Heading, Text, TextLink, Button as DSButton } from '@frontend/design-system';
import { useRetryer } from '../../hooks/use-retryer';
import { useSoftphoneClient } from '../../providers/softphone-client-provider';

const StatusPageLayout = ({ close, children }: { close: () => void; children: ReactNode }) => {
  return (
    <div style={{ display: 'grid', gridTemplateRows: 'auto 1fr' }}>
      <DSButton
        iconName='x'
        variant='secondary'
        css={{
          width: 'fit-content',
          justifySelf: 'end',
          '&:hover:not(:disabled)': {
            backgroundColor: theme.colors.neutral80,
          },
          svg: {
            color: theme.colors.white,
          },
        }}
        onClick={close}
      />
      {children}
    </div>
  );
};

export const SoftphoneLoadingPage = ({ close }: { close: () => void }) => {
  const { t } = useTranslation('softphone');
  return (
    <StatusPageLayout close={close}>
      <main>
        <ContentLoader
          message={t('Initializing Softphone...')}
          show={true}
          css={css`
            background: transparent;
            p {
              color: ${theme.colors.white};
            }
          `}
        />
      </main>
    </StatusPageLayout>
  );
};

const MAX_RETRIES = 3;

export const SoftphoneErrorPage = ({
  error,
  close,
  allowRetry = true,
  showRestart = true,
  heading,
  description,
}: {
  error?: Error;
  close: () => void;
  allowRetry?: boolean;
  showRestart?: boolean;
  heading: string;
  description?: string;
}) => {
  const { t } = useTranslation('softphone');
  const restart = useSoftphoneClient((ctx) => ctx.restart);
  const reconnect = useSoftphoneClient((ctx) => ctx.reconnect);
  const retryCount = useRef(0);
  const shouldRetry = allowRetry && retryCount.current < MAX_RETRIES;

  const { timeUntilNextRetry, lastRetryIsLoading } = useRetryer({
    interval: 5000,
    resume: shouldRetry,
    callback: () => {
      reconnect();
      retryCount.current++;
    },
    countdownFidelity: 250,
  });

  const secondsRemaining = ((timeUntilNextRetry % 60000) / 1000).toFixed(0);

  return (
    <StatusPageLayout close={close}>
      <main css={loadingPageStyle}>
        <div className='error-state-icon-container'>
          <DialPadErrorState />
        </div>
        <div style={{ display: 'grid', gap: theme.spacing(2) }}>
          <Heading textAlign='center' color='white' level={3}>
            {heading}
          </Heading>
          {error && (
            <Text color='error' textAlign='center'>
              {error?.message}
            </Text>
          )}
          <Text textAlign='center' color='white'>
            {description ?? (
              <Trans>
                <span>
                  Please try restarting your Softphone. If the issue persists, please contact{' '}
                  <TextLink href='https://www.weavehelp.com/hc/en-us/p/contact-us' color='primary'>
                    Weave Support
                  </TextLink>
                  .
                </span>
              </Trans>
            )}
          </Text>
        </div>
        {showRestart && <Button.Rect onClick={restart}>{t('Restart Softphone')}</Button.Rect>}
        {shouldRetry && !lastRetryIsLoading && (
          <Text color='white'>
            {t('Retrying in')} {secondsRemaining}...
          </Text>
        )}
      </main>
    </StatusPageLayout>
  );
};

const loadingPageStyle = css`
  background: ${theme.colors.neutral[90]};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100%;
  min-height: ${theme.spacing(75)};

  .error-state-icon-container {
    border-radius: 50%;
    background: white;
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${theme.spacing(25)};
    height: ${theme.spacing(25)};
  }

  & > * {
    margin-bottom: ${theme.spacing(3)};
  }
`;
